import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { config } from '../config';

/**
 * Remplissage automatique des formulaires pour faciliter les essais (mode `dev` uniquement : jamais en recette ni en
 * production). Ne remplit que les champs vides et visibles, respecte les contraintes de chaque champ (longueur,
 * bornes, motif) et passe par les événements natifs pour que React voie les valeurs comme une saisie.
 */

const NOMS = [
  'MBOUMBA',
  'NDONG',
  'ESSONO',
  'OBAME',
  'MOUSSAVOU',
  'NZE',
  'BIYOGO',
  'OYANE',
  'NGUEMA',
  'MAKAYA',
];
const PRENOMS_F = ['Clarisse', 'Prisca', 'Aurélie', 'Larissa', 'Sandrine', 'Nadège', 'Gisèle'];
const PRENOMS_H = ['Jean-Pierre', 'Ghislain', 'Davy', 'Fabrice', 'Ulrich', 'Serge', 'Hervé'];
const LIEUX = ['Libreville', 'Port-Gentil', 'Franceville', 'Oyem', 'Mouila', 'Lambaréné'];

const pick = <T,>(list: readonly T[]): T => list[Math.floor(Math.random() * list.length)]!;
const pad = (n: number) => String(n).padStart(2, '0');
const ascii = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z]/g, '');

function persona() {
  const female = Math.random() < 0.5;
  const year = 1960 + Math.floor(Math.random() * 40);
  const month = 1 + Math.floor(Math.random() * 12);
  const day = 1 + Math.floor(Math.random() * 28);
  return {
    nom: pick(NOMS),
    prenoms: female ? pick(PRENOMS_F) : pick(PRENOMS_H),
    sexe: female ? 'F' : 'M',
    annee: year,
    mois: month,
    jour: day,
    naissance: `${year}-${pad(month)}-${pad(day)}`,
    lieu: pick(LIEUX),
    pere: `${pick(PRENOMS_H)} ${pick(NOMS)}`,
    mere: `${pick(PRENOMS_F)} ${pick(NOMS)}`,
    ref: `LBV-${2020 + Math.floor(Math.random() * 6)}-${String(100 + Math.floor(Math.random() * 900)).padStart(4, '0')}`,
  };
}
type Persona = ReturnType<typeof persona>;

function recentDate(): string {
  const d = new Date(Date.now() - (30 + Math.floor(Math.random() * 600)) * 86_400_000);
  return d.toISOString().slice(0, 10);
}

function labelOf(el: HTMLElement): string {
  const id = el.getAttribute('id');
  const byFor = id ? document.querySelector(`label[for="${CSS.escape(id)}"]`) : null;
  const text =
    byFor?.textContent ??
    el.closest('label')?.textContent ??
    el.closest('.field')?.querySelector('label, span')?.textContent ??
    el.getAttribute('aria-label') ??
    el.getAttribute('placeholder') ??
    '';
  return `${text} ${el.getAttribute('name') ?? ''} ${id ?? ''}`.toLowerCase();
}

function textFor(label: string, p: Persona): string {
  const has = (...k: string[]) => k.some((w) => label.includes(w));
  if (has('courriel', 'e-mail', 'email')) return `${ascii(p.prenoms)}.${ascii(p.nom)}@exemple.ga`;
  if (has('année', 'annee')) return String(p.annee);
  if (has('mois')) return String(p.mois);
  if (has('jour')) return String(p.jour);
  if (has('téléphone', 'telephone', 'mobile')) return '+241 07 12 34 56';
  if (has('prénom', 'prenom')) return p.prenoms;
  if (has('père', 'pere')) return p.pere;
  if (has('mère', 'mere')) return p.mere;
  if (has('pays')) return 'Gabon';
  if (has('lieu', 'ville')) return p.lieu;
  if (has('nom')) return p.nom;
  if (has('naissance')) return String(p.annee);
  if (has('référence', 'reference')) return p.ref;
  if (has('qualification', 'infraction'))
    return pick(['Vol simple', 'Abus de confiance', 'Escroquerie', 'Recel']);
  if (has('nature')) return 'Jugement contradictoire';
  if (has('autorité', 'autorite', 'juridiction')) return 'Tribunal de première instance de Libreville';
  if (has('preuve', 'acte', 'pièce justificative')) return 'Copie certifiée conforme de l’acte';
  if (has('fondement', 'base légale', 'disposition')) return 'Loi d’amnistie n° 012/2025 du 4 mars 2025';
  if (has('source', 'minute')) return `Minute ${p.ref}`;
  if (has('détenteur', 'detenteur', 'service'))
    return 'Greffe du tribunal de première instance de Libreville';
  if (has('série', 'serie', 'registre')) return 'Registre des jugements correctionnels';
  if (has('emplacement', 'localisation', 'magasin')) return 'Magasin B, travée 3';
  if (has('lacune')) return 'Aucune lacune connue';
  if (has('organisme')) return 'Direction des ressources humaines';
  if (has('motif', 'raison', 'justification', 'observation', 'note', 'commentaire', 'description', 'objet'))
    return 'Vérification effectuée sur pièces, conforme au dossier.';
  return 'Information complétée pour l’essai';
}

function numberFor(label: string, el: HTMLInputElement, p: Persona): number {
  const has = (...k: string[]) => k.some((w) => label.includes(w));
  let n: number;
  if (has('année', 'annee')) n = p.annee;
  else if (has('jour')) n = p.jour;
  else if (has('mois') && !has('durée', 'duree', 'peine')) n = p.mois;
  else if (has('durée', 'duree', 'peine', 'mois')) n = 6;
  else if (has('montant', 'xaf', 'amende')) n = 50000;
  else if (has('page')) n = 4;
  else n = 1;
  const min = el.min === '' ? -Infinity : Number(el.min);
  const max = el.max === '' ? Infinity : Number(el.max);
  return Math.min(max, Math.max(min, n));
}

/** Petit PDF valide (en-tête %PDF, une page vide) ou PNG 1×1, selon ce que le champ accepte. */
function sampleFile(accept: string): File {
  if (/image|png|jpe?g/.test(accept) && !/pdf/.test(accept)) {
    const png = Uint8Array.from(
      atob(
        'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8z8BQDwAEhQGAhKmMIQAAAABJRU5ErkJggg==',
      ),
      (c) => c.charCodeAt(0),
    );
    return new File([png], 'piece.png', { type: 'image/png' });
  }
  const body =
    '%PDF-1.4\n1 0 obj<</Type/Catalog/Pages 2 0 R>>endobj\n2 0 obj<</Type/Pages/Kids[3 0 R]/Count 1>>endobj\n' +
    '3 0 obj<</Type/Page/Parent 2 0 R/MediaBox[0 0 595 842]>>endobj\ntrailer<</Root 1 0 R>>\n%%EOF\n';
  return new File([body], 'piece.pdf', { type: 'application/pdf' });
}

function setValue(el: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement, value: string) {
  const proto = Object.getPrototypeOf(el) as object;
  Object.getOwnPropertyDescriptor(proto, 'value')?.set?.call(el, value);
  el.dispatchEvent(new Event('input', { bubbles: true }));
  el.dispatchEvent(new Event('change', { bubbles: true }));
}

const visible = (el: HTMLElement) => el.offsetParent !== null || el.getClientRects().length > 0;

function fillAll(root: HTMLElement): number {
  const p = persona();
  let count = 0;
  const fields = root.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
    'input, textarea, select',
  );
  const radioGroups = new Set<string>();
  for (const el of fields) {
    if (el.disabled || el.closest('[data-autofill="off"]')) continue;
    if (el instanceof HTMLInputElement) {
      const type = el.type;
      if (['hidden', 'submit', 'button', 'search', 'password', 'reset'].includes(type)) continue;
      if (el.autocomplete === 'one-time-code' || el.autocomplete === 'username') continue;
      if (type === 'file') {
        // Toujours un fichier neuf : après un envoi, le champ garde l'ancien fichier sans que l'application le voie.
        const dt = new DataTransfer();
        dt.items.add(sampleFile(el.accept));
        el.files = dt.files;
        el.dispatchEvent(new Event('change', { bubbles: true }));
        count += 1;
        continue;
      }
      if (!visible(el)) continue;
      if (type === 'checkbox') {
        const label = labelOf(el);
        if (!el.checked && (el.required || /consent|atteste|certifie|accepte|reconna/.test(label))) {
          el.click();
          count += 1;
        }
        continue;
      }
      if (type === 'radio') {
        if (radioGroups.has(el.name)) continue;
        radioGroups.add(el.name);
        const group = root.querySelectorAll<HTMLInputElement>(
          `input[type="radio"][name="${CSS.escape(el.name)}"]`,
        );
        if (![...group].some((r) => r.checked)) {
          el.click();
          count += 1;
        }
        continue;
      }
      if (el.value !== '') continue;
      const label = labelOf(el);
      let value: string;
      if (type === 'number') value = String(numberFor(label, el, p));
      else if (type === 'date') value = label.includes('naissance') ? p.naissance : recentDate();
      else if (type === 'datetime-local' || type === 'time') continue;
      else if (type === 'email') value = textFor('courriel', p);
      else if (type === 'tel') value = textFor('téléphone', p);
      else value = textFor(label, p);
      if (el.maxLength > 0) value = value.slice(0, el.maxLength);
      if (el.minLength > 0 && value.length < el.minLength) value = value.padEnd(el.minLength, '.');
      if (el.pattern && !new RegExp(`^(?:${el.pattern})$`).test(value)) continue;
      setValue(el, value);
      count += 1;
    } else if (el instanceof HTMLTextAreaElement) {
      if (el.value !== '' || !visible(el)) continue;
      setValue(el, textFor(labelOf(el), p));
      count += 1;
    } else if (el instanceof HTMLSelectElement) {
      if (!visible(el) || el.value !== '') continue;
      const option = [...el.options].find((o) => o.value !== '' && !o.disabled);
      if (!option) continue;
      setValue(el, option.value);
      count += 1;
    }
  }
  return count;
}

export function AutoFillButton({ root }: { root: React.RefObject<HTMLElement | null> }) {
  const location = useLocation();
  const [hasForm, setHasForm] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    const el = root.current;
    if (!el || config.authMode !== 'dev') return;
    const check = () =>
      setHasForm(el.querySelector('input:not([type=hidden]):not([type=search]), textarea, select') !== null);
    check();
    const observer = new MutationObserver(check);
    observer.observe(el, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [root, location.pathname]);

  const shown = config.authMode === 'dev' && hasForm && location.pathname !== '/connexion';
  // Réserve la place du bouton flottant en bas de page : il ne masque jamais les derniers champs ni les boutons.
  useEffect(() => {
    document.body.classList.toggle('with-autofill', shown);
    return () => document.body.classList.remove('with-autofill');
  }, [shown]);

  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => setMessage(null), 2500);
    return () => clearTimeout(t);
  }, [message]);

  if (!shown) return null;

  const run = () => {
    if (!root.current) return;
    // Deux passes : un premier choix (type de demande, onglet) peut faire apparaître de nouveaux champs.
    const first = fillAll(root.current);
    setTimeout(() => {
      const second = root.current ? fillAll(root.current) : 0;
      const total = first + second;
      setMessage(total > 0 ? `${total} champ(s) rempli(s)` : 'Tous les champs sont déjà remplis');
    }, 150);
  };

  return (
    <div className="autofill" role="region" aria-label="Remplissage automatique">
      {message && (
        <span className="autofill-msg" role="status">
          {message}
        </span>
      )}
      <button
        className="btn autofill-btn"
        type="button"
        onClick={run}
        title="Remplir les champs vides du formulaire"
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="m15 4 5 5L9 20H4v-5zM13 6l5 5" />
        </svg>
        Remplir les champs
      </button>
    </div>
  );
}
